import {
  UserSurveyPrimaryFunction,
  UserSurveyRole,
} from 'Generated/graphql-types'
import { IntakeFlowStep, Priority } from './constants'
import { Welcome } from './components/Welcome/Welcome'
import { WhatYouDo } from './components/WhatYouDo/WhatYouDo'
import { OrgSummary } from './components/OrgSummary/OrgSummary'
import { ImpactAreas } from './components/ImpactAreas/ImpactAreas'
import { TopPriorities } from './components/TopPriorities/TopPriorities'
import { ConfidenceRating } from './components/ConfidenceRating/ConfidenceRating'
import { OtherFocusAreas } from './components/OtherFocusAreas/OtherFocusAreas'
import { LoadingImpactPlan } from './components/LoadingImpactPlan/LoadingImpactPlan'
import { ImpactPlan } from './components/ImpactPlan/ImpactPlan'
import { NeedProfilerInfo } from './components/NeedProfilerInfo/NeedProfilerInfo'

type WizardConfigArgs = {
  onNextStep: (nextStep: IntakeFlowStep) => void
  onBackStep: () => void
  userName?: string
  orgName?: string
  userJourneyLength: number
  userPrimaryFunction?: UserSurveyPrimaryFunction
  onSetUserPrimaryFunction: (primaryFunction: UserSurveyPrimaryFunction) => void
  userRole?: UserSurveyRole
  onSetUserRole: (role: UserSurveyRole) => void
  otherFocusAreas: string
  onSetOtherFocusAreas: (challenge: string) => void
  impactAreas: string[]
  onUpdateImpactAreas: (areas: string[]) => void
  priorities: Priority[]
  onUpdatePriorities: (priorities: Priority[]) => void
  orgSummary: string
  onUpdateOrgSummary: (summary: string) => void
  onSaveOrgSummary?: (summary: string) => void
  confidenceRatings: Record<string, number>
  onUpdateConfidence: (confidenceRatings: Record<string, number>) => void
  onMoreProfileInfoSave: (websiteUrl: string, ein: string) => void
  ein?: string
  websiteUrl?: string
}

interface StepConfig {
  component: JSX.Element
  header: string
  description?: string
  height?: string
  width?: string
  showAutoFixIcon?: boolean
}

export const createWizardConfig = ({
  onNextStep,
  onBackStep,
  userName = 'there',
  orgName = 'your organization',
  userJourneyLength = 0,
  userPrimaryFunction,
  onSetUserPrimaryFunction,
  userRole,
  onSetUserRole,
  otherFocusAreas,
  onSetOtherFocusAreas,
  impactAreas,
  onUpdateImpactAreas,
  priorities,
  onUpdatePriorities,
  orgSummary,
  onUpdateOrgSummary,
  onSaveOrgSummary,
  confidenceRatings,
  onUpdateConfidence,
  onMoreProfileInfoSave,
  ein,
  websiteUrl,
}: WizardConfigArgs): Record<IntakeFlowStep, StepConfig> => ({
  [IntakeFlowStep.WELCOME]: {
    component: <Welcome onNextStep={onNextStep} />,
    header: `Welcome to Resilia, ${userName}!`,
    width: '1040px',
    showAutoFixIcon: true,
  },
  [IntakeFlowStep.WHAT_YOU_DO]: {
    component: (
      <WhatYouDo
        onNextStep={onNextStep}
        onBackStep={onBackStep}
        onSetUserRole={onSetUserRole}
        onSetUserPrimaryFunction={onSetUserPrimaryFunction}
        userPrimaryFunction={userPrimaryFunction}
        userRole={userRole}
        userJourneyLength={userJourneyLength}
      />
    ),
    header: `How would you describe what you do at ${orgName}?`,
    description: 'Select the option that best describes your role.',
    width: '1040px',
    showAutoFixIcon: true,
  },
  [IntakeFlowStep.NEED_PROFILER_INFO]: {
    component: (
      <NeedProfilerInfo
        onNextStep={onNextStep}
        onBackStep={onBackStep}
        onMoreProfileInfoSave={onMoreProfileInfoSave}
        ein={ein}
        websiteUrl={websiteUrl}
      />
    ),
    header: `We'd like to know more about you.`,
    description: `Please ${
      ein || websiteUrl ? 'confirm' : 'share'
    } your EIN and website so that we can learn more about your activities and programs.`,
    width: '1040px',
    showAutoFixIcon: true,
  },
  [IntakeFlowStep.ORG_SUMMARY]: {
    component: (
      <OrgSummary
        onNextStep={onNextStep}
        onBackStep={onBackStep}
        orgSummary={orgSummary}
        onUpdateOrgSummary={onUpdateOrgSummary}
        onSaveOrgSummary={onSaveOrgSummary}
      />
    ),
    header:
      'We put together a quick overview of your activities and programs from public sources.',
    description:
      'Does this sound right? Feel free to edit the summary to best describe your work.',
    width: '1040px',
    showAutoFixIcon: true,
  },
  [IntakeFlowStep.IMPACT_AREAS]: {
    component: (
      <ImpactAreas
        onNextStep={onNextStep}
        onBackStep={onBackStep}
        impactAreas={impactAreas}
        onUpdateImpactAreas={onUpdateImpactAreas}
      />
    ),
    header: 'Impact Areas',
    description: "Select up to 3 areas where you'd like to focus your impact.",
    width: '800px',
    showAutoFixIcon: true,
  },
  [IntakeFlowStep.TOP_PRIORITIES]: {
    component: (
      <TopPriorities
        onNextStep={onNextStep}
        onBackStep={onBackStep}
        selectedPriorities={priorities}
        onUpdatePriorities={onUpdatePriorities}
      />
    ),
    header:
      "Select 3 topics you'd like to focus on and track progress towards in the next 3-6 months.",
    description:
      'These priorities will jumpstart your impact plan, but you can add additional priorities anytime.',
    width: '1040px',
    showAutoFixIcon: true,
  },
  [IntakeFlowStep.CONFIDENCE_RATING]: {
    component: (
      <ConfidenceRating
        onNextStep={onNextStep}
        onBackStep={onBackStep}
        priorities={priorities}
        onUpdateConfidence={onUpdateConfidence}
        confidenceRatings={confidenceRatings} // Pass existing confidence ratings
      />
    ),
    header: 'Rate your confidence on the 3 priorities you selected.',
    description:
      'This rating informs our recommendations and serves as diagnostic data as we track your organizational progress and growth. Use the slider to share how confident (5) or not confident (1) you feel.',
    width: '1040px',
    showAutoFixIcon: true,
  },
  [IntakeFlowStep.OTHER_FOCUS_AREAS]: {
    component: (
      <OtherFocusAreas
        onNextStep={onNextStep}
        onBackStep={onBackStep}
        onSetOtherFocusAreas={onSetOtherFocusAreas}
        otherFocusAreas={otherFocusAreas}
      />
    ),
    header: "Have a unique challenge you'd like support with? (Optional)",
    description:
      "Tell us about the unique challenges your team needs help navigating, and we'll further tailor your impact plan to match your needs.",
    width: '1040px',
    showAutoFixIcon: true,
  },
  [IntakeFlowStep.LOADING_IMPACT_PLAN]: {
    component: <LoadingImpactPlan onNextStep={onNextStep} />,
    header: `Impact Plan Loading`,
    width: '800px',
    // height: '504px',
    showAutoFixIcon: false,
  },
  [IntakeFlowStep.IMPACT_PLAN]: {
    component: <ImpactPlan priorities={priorities} />,
    header: `Huzzuh! Here's your impact plan, ${userName}.`,
    description:
      'Choose the  path that makes the most sense for you or explore Resilia’s ecosystem on your own.',
    width: '1064px',
    showAutoFixIcon: true,
  },
})
